import { createSlice } from '@reduxjs/toolkit'

import {
    determinePaidFeatures,
    extractMetadataFromSubscriptions,
    resolveMetadata,
} from '@/common/helpers/organisationHelpers'
import { getCurrencySymbol } from '@/common/utils'

import { organisationApi } from '../services/organisation'

const initialState = {
    organisation: {},
    organisationId: undefined,
    lastPaymentStatus: undefined,
    paidFeatures: undefined,
    useImperialUnits: undefined,
    currencySymbol: undefined,
    currencyCode: undefined,
    isOnTrial: false,
    reduceTrialAccess: false,
    planMetadata: undefined,
    resolvedPlanMetadata: undefined,
    isOnFreePlan: false,
    showAccelerationSettings: false,
    defaultTaxRateId: undefined,
    hasActiveSubscription: false,
    organisationSubdomain: undefined,
}

export const organisationSlice = createSlice({
    name: 'organisationSlice',
    initialState,
    reducers: {
        setOrganisation: (state, { payload }) => {
            const isOnTrialValue = (payload?.trial && !payload?.trial.hasExpired) ?? false
            const isOnFreePlan = Boolean(payload?.freePlan)
            const reduceTrialAccessValue =
                ((payload?.trial &&
                    !payload?.trial?.hasExpired &&
                    !payload.hasActiveSubscription &&
                    !import.meta.env.VITE_RESELLER) ||
                    (payload?.freePlan && !payload.hasActiveSubscription && !import.meta.env.VITE_RESELLER)) ??
                false

            // Use the helper functions to process the metadata and features
            const metadata = extractMetadataFromSubscriptions(payload?.subscriptions)
            const resolvedMetadata = resolveMetadata(metadata)
            const paidFeatures = determinePaidFeatures(resolvedMetadata, isOnTrialValue, payload?.hasActiveSubscription)

            const useImperialUnits =
                payload?.defaultDrawingUnits && payload?.defaultDrawingUnits?.toLowerCase() === 'imperial'

            const currencySymbol = getCurrencySymbol(payload?.currencyCode, payload?.locale)

            state.resolvedPlanMetadata = resolvedMetadata
            state.planMetadata = metadata
            state.paidFeatures = paidFeatures
            state.useImperialUnits = useImperialUnits
            state.currencySymbol = currencySymbol
            state.currencyCode = payload.currencyCode
            state.reduceTrialAccess = reduceTrialAccessValue
            state.isOnTrial = isOnTrialValue
            state.isOnFreePlan = isOnFreePlan
            state.organisationId = payload.organisationId
            state.organisation = payload
            state.hasActiveSubscription = payload.hasActiveSubscription
            state.showAccelerationSettings = payload.showAccelerationSettings
        },
        setOrganisationId: (state, action) => {
            state.organisationId = action.payload
        },
        setLastPaymentStatus: (state, action) => {
            state.lastPaymentStatus = action.payload
        },
        setPaidFeatures: (state, action) => {
            state.paidFeatures = action.payload
        },
        setUseImperialUnits: (state, action) => {
            state.useImperialUnits = action.payload
        },
        setCurrencySymbol: (state, action) => {
            state.currencySymbol = action.payload
        },
        setcurrencyCode: (state, action) => {
            state.currencyCode = action.payload
        },
        setIsOnTrial: (state, action) => {
            state.isOnTrial = action.payload
        },
        setDefaultTaxRateId: (state, action) => {
            state.defaultTaxRateId = action.payload
        },
        setOrganisationSubdomain: (state, action) => {
            state.organisationSubdomain = action.payload
        },
    },
    extraReducers: (builder) => {
        builder
            .addMatcher(organisationApi.endpoints.getOrganisation.matchFulfilled, (state, payload) => {
                organisationSlice.caseReducers.setOrganisation(state, payload)
            })
            .addMatcher(organisationApi.endpoints.getOrganisationIdFromSubdomain.matchFulfilled, (state, payload) => {
                organisationSlice.caseReducers.setOrganisationId(state, payload)
            })
    },
})

export const selectOrganisation = (state) => state.organisationSlice.organisation
export const selectOrganisationId = (state) => state.organisationSlice.organisationId
export const selectPlanMetadata = (state) => state.organisationSlice.planMetadata
export const selectLastPaymentStatus = (state) => state.organisationSlice.lastPaymentStatus
export const selectUseImperialUnits = (state) => state.organisationSlice.useImperialUnits
export const selectCurrencySymbol = (state) => state.organisationSlice.currencySymbol
export const selectCurrencyCode = (state) => state.organisationSlice.currencyCode
export const selectPaidFeatures = (state) => state.organisationSlice.paidFeatures
export const selectIsOnTrial = (state) => state.organisationSlice.isOnTrial
export const selectReduceTrialAccess = (state) => state.organisationSlice.reduceTrialAccess
export const selectResolvedPlanMetadata = (state) => state.organisationSlice.resolvedPlanMetadata
export const selectIsOnFreePlan = (state) => state.organisationSlice.isOnFreePlan
export const selectShowAccelerationSettings = (state) => state.organisationSlice.showAccelerationSettings
export const selectDefaultTaxRateId = (state) => state.organisationSlice.defaultTaxRateId
export const selectMaxUsers = (state) => state.organisationSlice.resolvedPlanMetadata?.max_users ?? 0
export const selectHasActiveSubscription = (state) => state.organisationSlice.hasActiveSubscription
export const selectOrganisationSubdomain = (state) => state.organisationSlice.organisationSubdomain

export const {
    setCurrencySymbol,
    setDefaultTaxRateId,
    setIsOnTrial,
    setLastPaymentStatus,
    setOrganisation,
    setOrganisationId,
    setOrganisationSubdomain,
    setPaidFeatures,
    setUseImperialUnits,
} = organisationSlice.actions

export default organisationSlice.reducer
